import _ from 'lodash';
import {
  BasicTelcoItemGTMData,
  BasketItemInfo,
  ProductImpression,
  PurchaseItemGTMData,
  TelcoItemOverviewInfo,
  TelcoItemTPInfo,
} from '../app/store/analytics/types';
import { IProductDataState } from '../app/store/product/productOverview/types';
import { AppState } from '../app/store/rootReducers';
import { ISearchParamsPagesState } from '../app/store/searchParams/types';
import { IBasketOrderItem } from '../entities/manhattan/order/types';
import { Purchases } from '../constants/Purchases';
import { IProduct, IProductPrepared, ProductColorsImage } from '../entities/product/types';
import { removeDotFromPrice } from './util';
import Accessories from '../constants/Accessories';
import { OrderItemsTypes } from '../constants/OrderItemsTypes';
import { OrderTypes } from '../constants/OrderTypes';

export const normalizePrice = (val: string | number) => (+val > 0 ? +val / 100 : 0);

export const getColorNameFromProduct = (product: IProduct, colorId: number | string): string => {
  const { productColorsImages } = product;
  const colors = ((productColorsImages as ProductColorsImage[]) || []).map((ci) => ci.color);

  return _.first(colors.filter((c) => c.id === Number(colorId)))?.label ?? '';
};

export const getBasicTelcoItemData = (product: IProductPrepared): BasicTelcoItemGTMData => {
  return {
    name: `${product.manufacturer?.name} ${product.model}`,
    id: product.phoneId ?? product.id,
    price: Number(removeDotFromPrice(product.displayCreditPrice)),
    brand: product.manufacturer?.name,
    category: product.productLine?.productLine,
  };
};

export const createFilterStringFromParams = (
  searchParams: ISearchParamsPagesState['productList'],
  tariffPackages: any[]
): string => {
  const { brand, dualSim, eSim, opsystems, orderBy, ramMemory, screenSize, tpackage } = searchParams;
  const filterList = [];

  filterList.push(brand);

  if (dualSim === 'true') filterList.push('DUAL SIM DA');
  if (dualSim === 'false') filterList.push('DUAL SIM NE');
  if (eSim === 'true') filterList.push('E SIM DA');
  if (eSim === 'false') filterList.push('E SIM NE');
  if (opsystems) filterList.push(opsystems.toUpperCase());
  if (orderBy) filterList.push(orderBy.toUpperCase());
  if (ramMemory) {
    filterList.push(ramMemory.replace('>', 'GT').replace('<', 'LT'));
  }
  if (screenSize) {
    filterList.push(screenSize.replace('>', 'GT').replace('<', 'LT')
      .replace('"', 'INCH'));
  }
  if (tpackage) filterList.push(tariffPackages.find((tp: any) => tp.id === Number(tpackage))?.name);

  return filterList.join(' | ');
};

export const createImpressionForItem = (
  searchParams: ISearchParamsPagesState['productList'],
  product: IProductPrepared,
  position: number
): ProductImpression => {
  const { purchase } = searchParams;
  const listValue = [];

  listValue.push(purchase ? (purchase === 'cosmos' ? 'NA RATE' : 'STANDARD') : 'NA RATE');
  listValue.push(product.productLine.productLine.toUpperCase());
  if (product.outlet) {
    listValue.push('POSEBNA');
  }

  listValue.push(createFilterStringFromParams(searchParams, product.tariffPackages));

  return {
    ...getBasicTelcoItemData(product),
    dimension1: product.label?.name || '',
    position,
    list: listValue.filter((x) => x).join(' > '),
  };
};

export const getItemOverviewInfo = (
  product: IProductPrepared,
  productData: IProductDataState
): TelcoItemOverviewInfo => {
  return {
    ...getBasicTelcoItemData(product),
    variant: getColorNameFromProduct(product, productData.color),
  };
};

export const getBasketItemInfo = (product: IProductPrepared, productData: IProductDataState): BasketItemInfo => {
  return {
    ...getBasicTelcoItemData(product),
    variant: getColorNameFromProduct(product, productData.color),
    orderType: (productData.purchase as string).toUpperCase(),
    cosmosModel: (productData.cosmosModel as string).toUpperCase(),
    quantity: 1,
  };
};

export const getOverviewTPInfo = (product: IProductPrepared, productData: IProductDataState): TelcoItemTPInfo => {
  const { cosmosTariffPackages, subsidyTariffPackages } = product;
  const { purchase, cosmosInstallments, contract } = productData;
  const { COSMOS } = Purchases;

  let tariffPackageGroup;
  let tp;
  if (purchase === COSMOS) {
    tariffPackageGroup = cosmosTariffPackages;
  } else {
    tariffPackageGroup = subsidyTariffPackages;
  }

  if (!Accessories.includes(product.productLine?.productLine)) {
    tp = _.first(tariffPackageGroup?.filter((t) => t.id === productData.tariff));
  }

  if (!tp) {
    return {
      name: '',
      id: 0,
      price: 0,
      brand: 'YETTEL',
      category: 'PLAN',
      quantity: 0,
    };
  }

  const { name, id } = tp;

  return {
    name: name,
    id: id,
    price: purchase === COSMOS ? tp.cosmosSubscription : tp.subscription,
    brand: 'YETTEL',
    category: 'PLAN',
    quantity: purchase === COSMOS ? cosmosInstallments : contract,
  };
};

const getPriceForItem = (orderProduct: IBasketOrderItem): number => {
  if (orderProduct.type === OrderItemsTypes.MSISDN || orderProduct.type === OrderItemsTypes.COSMOS_PARTICIPATION) {
    return (orderProduct.price?.amount as number) / 100;
  }

  if ((orderProduct.price?.installments as number) > 0) {
    return (
      (orderProduct.price?.installments as number) *
      +normalizePrice(orderProduct.price?.installmentWithIncludedDiscount as number)
    );
  }

  return Number(orderProduct.price?.amount ? orderProduct.price?.amount / 100 : 0);
};

export const formatItemsForPurchase = (
  orderProductList: IBasketOrderItem[],
  productData: IProductDataState,
  orderType: string
): Partial<PurchaseItemGTMData>[] => {
  return orderProductList.map((orderProduct) => {
    const item = {} as PurchaseItemGTMData;

    if (orderProduct.type === OrderItemsTypes.PHONE) {
      item.category = 'PHONE';
      item.brand = orderProduct.manufacturer as string;
      item.name = orderProduct.name;
      item.quantity = 1;
      item.variant = orderProduct.color?.color;

      if (orderType === OrderTypes.TELCO) {
        item.id = +productData.productId;
      }
    }

    if ([...Accessories].includes(orderProduct.type)) {
      item.category = orderProduct.type;
      item.brand = orderProduct.manufacturer as string;
      item.name = orderProduct.name;
      item.quantity = (orderProduct.quantity as number) || 1;
      item.id = +orderProduct.id;
    }

    if (orderProduct.type === OrderItemsTypes.STB) {
      item.category = 'STB';
      item.brand = 'YETTEL';
      item.name = orderProduct.name;
      item.quantity = 1;
    }

    if (orderProduct.type === OrderItemsTypes.COSMOS_PARTICIPATION) {
      item.category = 'CASH';
      item.brand = 'YETTEL';
      item.quantity = 1;
      item.price = 0;
    }

    if (orderProduct.type === OrderItemsTypes.TARIFF_PACKAGE || orderProduct.type === OrderItemsTypes.BUNDLE_PACKAGE) {
      item.category = 'PLAN';
      item.brand = 'YETTEL';
      item.name = orderProduct.name;
      item.quantity = orderProduct.price?.installments as number;
      item.price = 0;

      if (orderType === OrderTypes.TELCO) {
        item.id = productData.tariff;
      }
    }

    if (orderProduct.type === OrderItemsTypes.MSISDN) {
      item.category = 'MSISDN';
      item.brand = 'YETTEL';
      item.quantity = 1;
      item.price = 0;
    }

    if (orderProduct.type === OrderItemsTypes.SIM_ACTIVATION) {
      item.category = 'SIM ACTIVATION';
      item.brand = 'YETTEL';
      item.quantity = 1;
      item.price = 0;
    }

    if (orderProduct.type === OrderItemsTypes.INSURANCE) {
      item.category = 'INSURANCE';
      item.brand = 'YETTEL';
      item.quantity = 1;
      item.price = 0;
    }

    if (orderProduct.type === OrderItemsTypes.INSTALLATION_COST) {
      item.category = 'INSTALLATIION COST';
      item.brand = 'YETTEL';
      item.quantity = 1;
      item.price = 0;
    }

    if (orderProduct.type === OrderItemsTypes.DELIVERY_COST) {
      item.category = 'DELIVERY COST';
      item.brand = 'YETTEL';
      item.quantity = 1;
      item.price = 0;
    }

    item.price = getPriceForItem(orderProduct);

    return item;
  });
};

export const getUserIdAndAgentTypeFromState = (state: AppState): { userId: string; agentType: string } => {
  return {
    userId: state.session?.gtmData?.gtmUserId || '',
    agentType: state.session?.gtmData?.gtmAgentType || '',
  };
};
